import React from "react";
import { Mail, Globe, MapPin, MessageSquare, Clock, ShieldCheck } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import Seo, { ORG_JSON_LD } from "../components/marketing/Seo";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CONTACT_LD = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: "https://hellosir.ai/contact",
  name: "Contact HelloSir.ai",
  description:
    "Talk to the HelloSir.ai team — sales, support, partnerships and press. A product of Cloud Lab Technologies, United Kingdom.",
  publisher: ORG_JSON_LD,
};

export default function Contact() {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [topic, setTopic] = React.useState("sales");
  const [message, setMessage] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/contact", { name, email, topic, message });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send your message. Please try again or email hello@hellosir.ai.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingLayout>
      <Seo
        title="Contact Us"
        description="Talk to HelloSir.ai — sales, support, partnerships and press. We typically reply within one business day. A Cloud Lab Technologies (UK) product."
        keywords={["contact hellosir.ai", "AI chatbot sales", "AI calling agent support", "Cloud Lab Technologies", "UK SaaS support"]}
        path="/contact"
        jsonLd={CONTACT_LD}
      />

      {/* Hero */}
      <section className="border-b border-[#E2E8F0] bg-gradient-to-br from-[#F0F4FF] via-white to-[#FFF1F8]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <span className="text-xs font-mono uppercase tracking-widest text-[#0044FF]">// say hello</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl mt-3 leading-[0.95] tracking-tight">
            Let's talk about your agent.
          </h1>
          <p className="text-lg text-[#4B5563] mt-6 max-w-2xl">
            Sales, support, partnerships, press — pick a route below or send us a message. Real humans, no ticket-bot maze.
          </p>
        </div>
      </section>

      {/* Contact card grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
          {/* Form */}
          <div className="rounded-[6px] bg-white border border-[#E2E8F0] p-8">
            <h2 className="font-display font-bold text-2xl">Send us a message</h2>
            <p className="text-sm text-[#4B5563] mt-2">We reply within one business day.</p>

            {sent ? (
              <div className="mt-8 p-6 rounded-[4px] bg-[#10B981]/10 border border-[#10B981]/30 text-[#065F46]" data-testid="contact-success">
                <div className="font-display font-bold text-lg">Thanks — message received.</div>
                <p className="text-sm mt-2">Our team will be in touch shortly at the email you provided.</p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="mt-6 space-y-4"
                data-testid="contact-form"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField label="Your name" required>
                    <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="contact-name"
                           className="hs-input" />
                  </FormField>
                  <FormField label="Email" required>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="contact-email"
                           className="hs-input" />
                  </FormField>
                </div>
                <FormField label="What's this about?">
                  <select value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="contact-topic" className="hs-input">
                    <option value="sales">Sales / pricing</option>
                    <option value="support">Technical support</option>
                    <option value="partnership">Partnership</option>
                    <option value="press">Press / media</option>
                    <option value="other">Other</option>
                  </select>
                </FormField>
                <FormField label="Message" required>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} required rows={6} data-testid="contact-message"
                            className="hs-input" />
                </FormField>
                <button type="submit" disabled={busy} className="btn-primary w-full md:w-auto disabled:opacity-60" data-testid="contact-submit">
                  {busy ? "Sending…" : "Send message"}
                </button>
                <p className="text-xs text-[#4B5563]">
                  By submitting you agree to our <a href="/privacy" className="text-[#0044FF] hover:underline">Privacy Policy</a>.
                </p>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            <ContactCard Icon={Mail} title="Email" accent="#0044FF"
                         lines={["hello@hellosir.ai — general", "billing@hellosir.ai — invoices", "safety@hellosir.ai — AI safety"]} />
            <ContactCard Icon={MessageSquare} title="Live chat" accent="#10B981"
                         lines={["Use the bubble on this page — our own AI agent answers instantly and routes complex cases to a human."]} />
            <ContactCard Icon={MapPin} title="Headquarters" accent="#7C3AED"
                         lines={["Cloud Lab Technologies", "United Kingdom"]}
                         link={{ href: "https://www.cloudlabweb.com", label: "cloudlabweb.com" }} />
            <ContactCard Icon={Clock} title="Hours" accent="#F97316"
                         lines={["Monday – Friday · 09:00 – 18:00 GMT", "Email replies within 1 business day"]} />
            <ContactCard Icon={ShieldCheck} title="Security & abuse" accent="#EF4444"
                         lines={["Report vulnerabilities or abuse to security@hellosir.ai", "PGP key available on request"]} />
            <ContactCard Icon={Globe} title="Press & media" accent="#06B6D4"
                         lines={["press@hellosir.ai", "Boilerplate, logos and headshots on request"]} />
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">
        {label}{required && <span className="text-[#EF4444] ml-1">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function ContactCard({ Icon, title, accent, lines, link }) {
  return (
    <div className="rounded-[6px] bg-white border border-[#E2E8F0] p-5 flex gap-4 hover:border-[#030712] transition-colors">
      <div className="w-11 h-11 rounded-[6px] flex items-center justify-center shrink-0"
           style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="font-display font-bold text-base">{title}</div>
        <ul className="text-sm text-[#4B5563] mt-1 space-y-0.5">
          {lines.map((l) => <li key={l}>{l}</li>)}
        </ul>
        {link && (
          <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-sm text-[#0044FF] hover:underline mt-1 inline-block">
            {link.label} →
          </a>
        )}
      </div>
    </div>
  );
}
